import React, { useContext } from 'react';
import {
  FaAward,
  FaCalendarAlt,
  FaCheckCircle,
  FaFilePdf,
  FaFistRaised,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaMedal,
  FaShieldAlt,
  FaStar,
  FaUserFriends,
  FaWhatsapp,
} from 'react-icons/fa';
import { AppContext } from '../context/AppContext';

const EVENT_DATES = '19 - 21 Juni 2026';
const FALLBACK_LOCATION = 'GOR Beladiri Madani, Kota Palu';

const formatDate = (dateString) => {
  if (!dateString) return EVENT_DATES;
  return new Date(dateString).toLocaleDateString('id-ID', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const InformationPage = () => {
  const { settings, masterData, isLoading } = useContext(AppContext);
  const backendUrl = process.env.REACT_APP_BACKEND || process.env.REACT_APP_BACKEND_URL || '';
  const eventName = settings?.event_name || 'SULTENG OPEN VII';
  const location = settings?.location || FALLBACK_LOCATION;
  const contactPerson = settings?.contact_person || '+62 812-3456-7890';
  const waNumber = String(contactPerson).replace(/^0/, '62').replace(/\D/g, '');
  const notes = Array.isArray(settings?.important_notes) ? settings.important_notes : [];

  const handleDownload = (filename) => {
    if (!filename) return;
    window.open(`${backendUrl}/uploads/proposal_file/${filename}`, '_blank', 'noopener,noreferrer');
  };

  if (isLoading) {
    return (
      <div className="sulteng-info-loader">
        <div className="spinner-border text-success" role="status" />
        <span>Memuat informasi...</span>
        <style>{`
          .sulteng-info-loader {
            min-height: 70vh;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.8rem;
            color: #14532d;
            font-weight: 800;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="sulteng-information">
      <section className="sulteng-information__hero">
        <div className="sulteng-information__pattern" />
        <div className="container position-relative text-center">
          <span className="sulteng-information__badge">
            <FaInfoCircle /> Pusat Informasi
          </span>
          <h1>{eventName}</h1>
          <p>Informasi teknis pertandingan, jadwal, lokasi, dan dokumen resmi dalam satu halaman publik.</p>
        </div>
      </section>

      <section className="sulteng-information__content">
        <div className="container">
          <div className="row g-4 g-lg-5">
            <div className="col-12 col-lg-8">
              <article className="sulteng-info-panel">
                <div className="sulteng-info-panel__head">
                  <span><FaShieldAlt /></span>
                  <div>
                    <small>Pelaksanaan</small>
                    <h2>Detail Kejuaraan</h2>
                  </div>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-12 col-md-6">
                    <div className="sulteng-info-detail">
                      <FaCalendarAlt />
                      <div>
                        <small>Tanggal Acara</small>
                        <strong>{formatDate(settings?.event_date)}</strong>
                        <span>08:00 WITA - selesai</span>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="sulteng-info-detail">
                      <FaMapMarkerAlt />
                      <div>
                        <small>Lokasi Venue</small>
                        <strong>{location}</strong>
                        <a href={`https://maps.google.com/?q=${encodeURIComponent(location)}`} target="_blank" rel="noreferrer">
                          Lihat di Google Maps
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="sulteng-info-grid">
                  <div className="sulteng-match-card orange">
                    <div className="sulteng-match-card__title">
                      <FaFistRaised />
                      <h3>Kyorugi (Tanding)</h3>
                    </div>
                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <h4><FaStar /> Prestasi</h4>
                        <ul>
                          <li>Menggunakan digital scoring dan perangkat PSS.</li>
                          <li>Bagan pertandingan menggunakan sistem gugur.</li>
                          <li>Toleransi berat {settings?.weight_tolerance || 0.5} kg.</li>
                        </ul>
                      </div>
                      <div className="col-12 col-md-6">
                        <h4><FaAward /> Festival</h4>
                        <ul>
                          <li>Grouping disesuaikan berdasarkan umur dan berat.</li>
                          <li>Fokus pada pembinaan mental atlet pemula.</li>
                          <li>Setiap grup mengikuti ketentuan teknis panitia.</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="sulteng-match-card green">
                    <div className="sulteng-match-card__title">
                      <FaUserFriends />
                      <h3>Poomsae (Jurus)</h3>
                    </div>
                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <h4><FaStar /> Prestasi</h4>
                        <ul>
                          <li>Sistem cut-off atau tournament play sesuai technical meeting.</li>
                          <li>Kategori individual, pair, dan team.</li>
                          <li>Penilaian akurasi dan presentasi secara ketat.</li>
                        </ul>
                      </div>
                      <div className="col-12 col-md-6">
                        <h4><FaAward /> Festival</h4>
                        <ul>
                          <li>Jurus disesuaikan dengan tingkat sabuk.</li>
                          <li>Penilaian dasar mengikuti standar panitia.</li>
                          <li>Pengelompokan peserta mengikuti jumlah pendaftar.</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </article>

              <article className="sulteng-info-panel mt-4">
                <div className="sulteng-info-panel__head">
                  <span><FaCheckCircle /></span>
                  <div>
                    <small>Ketentuan Umum</small>
                    <h2>Catatan Teknis</h2>
                  </div>
                </div>

                <div className="row g-3">
                  {(notes.length > 0 ? notes : [
                    'Peserta wajib membawa ID card saat penimbangan dan pertandingan.',
                    'Perubahan data atlet mengikuti batas waktu yang ditetapkan panitia.',
                    'Keputusan technical meeting menjadi acuan pelaksanaan pertandingan.',
                  ]).map((note, index) => (
                    <div className="col-12" key={`${note}-${index}`}>
                      <div className="sulteng-note-row">
                        <span>{index + 1}</span>
                        <p>{note}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            </div>

            <div className="col-12 col-lg-4">
              <aside className="sulteng-info-sidebar">
                <div className="sulteng-help-card">
                  <FaWhatsapp />
                  <h2>Butuh Bantuan?</h2>
                  <p>Hubungi panitia jika ada kendala pendaftaran, pembayaran, atau validasi data atlet.</p>
                  {waNumber ? (
                    <a href={`https://wa.me/${waNumber}`} className="btn" target="_blank" rel="noreferrer">
                      Chat WhatsApp
                    </a>
                  ) : (
                    <button type="button" className="btn" disabled>Kontak Belum Tersedia</button>
                  )}
                </div>

                <div className="sulteng-side-card">
                  <h3><FaFilePdf /> Unduh Dokumen</h3>
                  <div className="d-grid gap-2">
                    <button
                      type="button"
                      className="btn sulteng-doc-btn"
                      disabled={!settings?.proposal_file}
                      onClick={() => handleDownload(settings?.proposal_file)}
                    >
                      Proposal Event
                    </button>
                    <button
                      type="button"
                      className="btn sulteng-doc-btn secondary"
                      disabled={!settings?.invitation_file}
                      onClick={() => handleDownload(settings?.invitation_file)}
                    >
                      Surat Undangan
                    </button>
                  </div>
                </div>

                <div className="sulteng-side-card">
                  <h3><FaMedal /> Ketentuan Umur</h3>
                  <div className="sulteng-division-list">
                    {masterData?.divisions?.length > 0 ? (
                      masterData.divisions.map((division) => (
                        <div className="sulteng-division-row" key={division.name}>
                          <strong>{division.name}</strong>
                          <span>{division.min_age} - {division.max_age === 0 ? 'DST' : division.max_age} Tahun</span>
                        </div>
                      ))
                    ) : (
                      <p className="sulteng-empty-text">Data divisi belum diatur.</p>
                    )}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .sulteng-information {
          min-height: 100vh;
          background: #f8fafc;
          color: #0f172a;
        }

        .sulteng-information__hero {
          position: relative;
          overflow: hidden;
          padding: 8.5rem 0 6rem;
          color: #ffffff;
          background: linear-gradient(135deg, #14532d, #052e16);
        }

        .sulteng-information__pattern {
          position: absolute;
          inset: 0;
          opacity: 0.08;
          background-image: radial-gradient(#facc15 10%, transparent 10%), radial-gradient(#ffffff 10%, transparent 10%);
          background-size: 42px 42px;
          background-position: 0 0, 21px 21px;
        }

        .sulteng-information__badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1rem;
          padding: 0.62rem 0.95rem;
          border: 1px solid rgba(250, 204, 21, 0.32);
          border-radius: 999px;
          color: #facc15;
          background: rgba(255, 255, 255, 0.1);
          font-size: 0.78rem;
          font-weight: 900;
          text-transform: uppercase;
          backdrop-filter: blur(12px);
        }

        .sulteng-information__hero h1 {
          max-width: 900px;
          margin: 0 auto 1rem;
          font-size: 3rem;
          font-weight: 900;
          line-height: 1.05;
          text-transform: uppercase;
        }

        .sulteng-information__hero p {
          max-width: 720px;
          margin: 0 auto;
          color: #bbf7d0;
          font-weight: 650;
          line-height: 1.75;
        }

        .sulteng-information__content {
          position: relative;
          margin-top: -3rem;
          padding-bottom: 5rem;
        }

        .sulteng-info-panel,
        .sulteng-side-card,
        .sulteng-help-card {
          border: 1px solid rgba(22, 101, 52, 0.1);
          border-radius: 26px;
          background: rgba(255, 255, 255, 0.95);
          box-shadow: 0 22px 50px rgba(15, 23, 42, 0.09);
          backdrop-filter: blur(14px);
        }

        .sulteng-info-panel {
          padding: 1.6rem;
        }

        .sulteng-info-panel__head {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .sulteng-info-panel__head > span {
          width: 58px;
          height: 58px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          border-radius: 18px;
          color: #14532d;
          background: #facc15;
          font-size: 1.35rem;
        }

        .sulteng-info-panel__head small {
          display: block;
          color: #ea580c;
          font-size: 0.75rem;
          font-weight: 900;
          text-transform: uppercase;
        }

        .sulteng-info-panel__head h2 {
          margin: 0;
          color: #14532d;
          font-size: 1.75rem;
          font-weight: 900;
          text-transform: uppercase;
        }

        .sulteng-info-detail {
          height: 100%;
          display: flex;
          gap: 1rem;
          padding: 1rem;
          border: 1px solid #e2e8f0;
          border-radius: 20px;
          background: #f8fafc;
        }

        .sulteng-info-detail > svg {
          width: 48px;
          height: 48px;
          padding: 0.8rem;
          flex: 0 0 auto;
          color: #ea580c;
          background: #ffedd5;
          border-radius: 16px;
        }

        .sulteng-info-detail small {
          display: block;
          color: #64748b;
          font-size: 0.7rem;
          font-weight: 900;
          text-transform: uppercase;
        }

        .sulteng-info-detail strong {
          display: block;
          color: #14532d;
          font-size: 1rem;
          font-weight: 900;
          line-height: 1.35;
        }

        .sulteng-info-detail span,
        .sulteng-info-detail a {
          display: inline-block;
          margin-top: 0.25rem;
          color: #64748b;
          font-size: 0.85rem;
          font-weight: 700;
          text-decoration: none;
        }

        .sulteng-info-detail a:hover {
          color: #ea580c;
        }

        .sulteng-info-grid {
          display: grid;
          gap: 1rem;
        }

        .sulteng-match-card {
          padding: 1.25rem;
          border: 1px solid #e2e8f0;
          border-left-width: 5px;
          border-radius: 22px;
          background: #ffffff;
        }

        .sulteng-match-card.orange {
          border-left-color: #ea580c;
        }

        .sulteng-match-card.green {
          border-left-color: #16a34a;
        }

        .sulteng-match-card__title {
          display: flex;
          align-items: center;
          gap: 0.7rem;
          margin-bottom: 1rem;
        }

        .sulteng-match-card__title svg {
          color: #ea580c;
          font-size: 1.5rem;
        }

        .sulteng-match-card.green .sulteng-match-card__title svg {
          color: #16a34a;
        }

        .sulteng-match-card h3 {
          margin: 0;
          color: #14532d;
          font-size: 1.25rem;
          font-weight: 900;
        }

        .sulteng-match-card h4 {
          display: flex;
          align-items: center;
          gap: 0.45rem;
          margin-bottom: 0.6rem;
          color: #14532d;
          font-size: 0.9rem;
          font-weight: 900;
          text-transform: uppercase;
        }

        .sulteng-match-card ul {
          margin: 0;
          padding-left: 1.05rem;
          color: #475569;
          font-size: 0.9rem;
          font-weight: 650;
          line-height: 1.7;
        }

        .sulteng-note-row {
          display: flex;
          gap: 0.85rem;
          align-items: flex-start;
          padding: 1rem;
          border-radius: 18px;
          color: #14532d;
          background: #f0fdf4;
        }

        .sulteng-note-row span {
          width: 32px;
          height: 32px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          border-radius: 999px;
          color: #ffffff;
          background: #166534;
          font-weight: 900;
        }

        .sulteng-note-row p {
          margin: 0;
          font-weight: 700;
          line-height: 1.65;
        }

        .sulteng-info-sidebar {
          display: grid;
          gap: 1rem;
          position: sticky;
          top: 6.5rem;
        }

        .sulteng-help-card {
          padding: 1.6rem;
          color: #ffffff;
          background: linear-gradient(135deg, #14532d, #052e16);
        }

        .sulteng-help-card > svg {
          color: #facc15;
          font-size: 2.4rem;
          margin-bottom: 1rem;
        }

        .sulteng-help-card h2 {
          margin-bottom: 0.7rem;
          font-size: 1.35rem;
          font-weight: 900;
        }

        .sulteng-help-card p {
          color: #bbf7d0;
          font-weight: 650;
          line-height: 1.7;
        }

        .sulteng-help-card .btn {
          width: 100%;
          border-radius: 999px;
          color: #14532d;
          background: #facc15;
          font-weight: 900;
          padding: 0.85rem 1rem;
        }

        .sulteng-help-card .btn:hover {
          color: #14532d;
          background: #fde047;
          transform: translateY(-2px);
        }

        .sulteng-side-card {
          padding: 1.4rem;
        }

        .sulteng-side-card h3 {
          display: flex;
          align-items: center;
          gap: 0.55rem;
          margin-bottom: 1rem;
          color: #14532d;
          font-size: 1rem;
          font-weight: 900;
          text-transform: uppercase;
        }

        .sulteng-side-card h3 svg {
          color: #ea580c;
        }

        .sulteng-doc-btn {
          border-radius: 16px;
          color: #ffffff;
          background: #166534;
          font-weight: 900;
          padding: 0.85rem 1rem;
        }

        .sulteng-doc-btn.secondary {
          background: #ea580c;
        }

        .sulteng-doc-btn:hover:not(:disabled) {
          color: #ffffff;
          transform: translateY(-2px);
          filter: brightness(1.05);
        }

        .sulteng-doc-btn:disabled {
          color: #94a3b8;
          background: #e2e8f0;
          opacity: 1;
        }

        .sulteng-division-list {
          display: grid;
          gap: 0.65rem;
        }

        .sulteng-division-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
          padding: 0.75rem 0.9rem;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          background: #f8fafc;
        }

        .sulteng-division-row strong {
          color: #14532d;
          font-size: 0.9rem;
          font-weight: 900;
        }

        .sulteng-division-row span {
          white-space: nowrap;
          border-radius: 999px;
          color: #334155;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          padding: 0.35rem 0.55rem;
          font-size: 0.75rem;
          font-weight: 800;
        }

        .sulteng-empty-text {
          margin: 0;
          color: #64748b;
          font-weight: 700;
          text-align: center;
        }

        @media (max-width: 991.98px) {
          .sulteng-info-sidebar {
            position: static;
          }

          .sulteng-information__hero h1 {
            font-size: 2.2rem;
          }
        }

        @media (max-width: 575.98px) {
          .sulteng-information__hero {
            padding-top: 7.5rem;
          }

          .sulteng-info-panel,
          .sulteng-side-card,
          .sulteng-help-card {
            border-radius: 22px;
          }

          .sulteng-info-panel {
            padding: 1.15rem;
          }

          .sulteng-info-panel__head h2 {
            font-size: 1.35rem;
          }

          .sulteng-division-row {
            align-items: flex-start;
            flex-direction: column;
            gap: 0.45rem;
          }
        }
      `}</style>
    </div>
  );
};

export default InformationPage;
